import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import {
  microLabelSx,
  tableHeadCellSx,
  tableBodyCellSx,
} from "../../utils/fdcTheme";

const PROJECTILES = ["HE", "SMOKE"];
const FUZES = ["PD", "TIME"];

const numField = (theme) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 1,
    height: 36,
    fontVariantNumeric: "tabular-nums",
  },
  "& input": { fontSize: 13, fontWeight: 600, py: 0.5, px: 1 },
});

/**
 * SafetyClearanceTool
 *
 * Lets the FDC define safe firing limits per (projectile, fuze) for a mission.
 * The Firing Data panel cross-checks each shot against these limits and gates the
 * Send-to-Guns action when a shot falls outside the envelope.
 *
 * Limits per (projectile, fuze):
 *   - QE min / max     (mils)
 *   - Direction left / right  (mils, defining the safe arc)
 *   - Fuze setting min  (only meaningful when fuze == TIME)
 */
export default function SafetyClearanceTool({ backendUrl, missionId }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [clearances, setClearances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Form state for adding/updating one clearance
  const [form, setForm] = useState({
    projectile: "HE",
    fuze_type: "PD",
    qe_min: "",
    qe_max: "",
    direction_left: "",
    direction_right: "",
    fuze_setting_min: "",
  });

  const isTime = form.fuze_type === "TIME";

  const fetchClearances = async () => {
    if (!missionId) {
      setClearances([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${backendUrl}/safety-clearances/${encodeURIComponent(missionId)}`);
      setClearances(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to load clearances.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClearances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missionId]);

  const setField = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const submit = async () => {
    setError("");
    setSuccessMessage("");
    if (!missionId) {
      setError("Select a mission first.");
      return;
    }
    const payload = {
      projectile: form.projectile,
      fuze_type: form.fuze_type,
      qe_min: form.qe_min === "" ? null : Number(form.qe_min),
      qe_max: form.qe_max === "" ? null : Number(form.qe_max),
      direction_left: form.direction_left === "" ? null : Number(form.direction_left),
      direction_right: form.direction_right === "" ? null : Number(form.direction_right),
      fuze_setting_min: !isTime || form.fuze_setting_min === "" ? null : Number(form.fuze_setting_min),
    };

    if (
      payload.qe_min !== null &&
      payload.qe_max !== null &&
      payload.qe_min > payload.qe_max
    ) {
      setError("QE min cannot be greater than QE max.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post(
        `${backendUrl}/safety-clearances/${encodeURIComponent(missionId)}`,
        payload,
      );
      if (res.data?.status !== "success") {
        throw new Error(res.data?.message || "Save failed");
      }
      setSuccessMessage(`Clearance saved · ${form.projectile} / ${form.fuze_type}`);
      await fetchClearances();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to save clearance.");
    } finally {
      setLoading(false);
    }
  };

  const removeClearance = async (c) => {
    const ok = window.confirm(
      `Delete safety clearance for ${c.projectile} / ${c.fuze_type}?`,
    );
    if (!ok) return;
    try {
      setLoading(true);
      await axios.delete(
        `${backendUrl}/safety-clearances/${encodeURIComponent(missionId)}/${encodeURIComponent(c.projectile)}/${encodeURIComponent(c.fuze_type)}`,
      );
      await fetchClearances();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to delete clearance.");
    } finally {
      setLoading(false);
    }
  };

  const editFromExisting = (c) => {
    setForm({
      projectile: c.projectile,
      fuze_type: c.fuze_type,
      qe_min: c.qe_min ?? "",
      qe_max: c.qe_max ?? "",
      direction_left: c.direction_left ?? "",
      direction_right: c.direction_right ?? "",
      fuze_setting_min: c.fuze_setting_min ?? "",
    });
  };

  const headerSx = useMemo(() => ({
    fontWeight: 700,
    fontSize: 14,
    letterSpacing: 0.2,
    display: "flex",
    alignItems: "center",
    gap: 0.75,
  }), []);

  return (
    <Stack spacing={1.25}>
      <Box>
        <Typography sx={headerSx}>
          <SecurityRoundedIcon sx={{ fontSize: 18, color: "warning.main" }} />
          Safety Clearance
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.25, display: "block" }}>
          Define the safe firing envelope per projectile + fuze. Fire elements that fall outside
          turn red and require an explicit override before being sent to the guns.
        </Typography>
      </Box>

      {!missionId && (
        <Alert severity="info" sx={{ fontWeight: 600 }}>
          Select a mission first to manage its safety clearances.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert>}
      {successMessage && <Alert severity="success" sx={{ py: 0.5 }}>{successMessage}</Alert>}

      {/* Form to add / update one clearance */}
      <Paper
        variant="outlined"
        sx={(t) => ({
          p: 1.25,
          borderRadius: 1.25,
          opacity: missionId ? 1 : 0.5,
          pointerEvents: missionId ? "auto" : "none",
        })}
      >
        <Stack spacing={1}>
          <Stack direction="row" spacing={1}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ ...microLabelSx, color: "text.secondary", mb: 0.4 }}>
                Projectile
              </Typography>
              <Select
                value={form.projectile}
                onChange={setField("projectile")}
                size="small"
                fullWidth
                sx={{ height: 36, fontSize: 13, fontWeight: 700 }}
              >
                {PROJECTILES.map((p) => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </Select>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ ...microLabelSx, color: "text.secondary", mb: 0.4 }}>
                Fuze
              </Typography>
              <Select
                value={form.fuze_type}
                onChange={setField("fuze_type")}
                size="small"
                fullWidth
                sx={{ height: 36, fontSize: 13, fontWeight: 700 }}
              >
                {FUZES.map((f) => (
                  <MenuItem key={f} value={f}>{f}</MenuItem>
                ))}
              </Select>
            </Box>
          </Stack>

          <Divider />

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            <Box>
              <Typography sx={{ ...microLabelSx, color: "text.secondary", mb: 0.4 }}>
                QE min (mils)
              </Typography>
              <TextField
                type="number" size="small" fullWidth
                value={form.qe_min} onChange={setField("qe_min")}
                placeholder="—" sx={numField(theme)}
              />
            </Box>
            <Box>
              <Typography sx={{ ...microLabelSx, color: "text.secondary", mb: 0.4 }}>
                QE max (mils)
              </Typography>
              <TextField
                type="number" size="small" fullWidth
                value={form.qe_max} onChange={setField("qe_max")}
                placeholder="—" sx={numField(theme)}
              />
            </Box>
            <Box>
              <Typography sx={{ ...microLabelSx, color: "text.secondary", mb: 0.4 }}>
                Direction left (mils)
              </Typography>
              <TextField
                type="number" size="small" fullWidth
                value={form.direction_left} onChange={setField("direction_left")}
                placeholder="—" sx={numField(theme)}
              />
            </Box>
            <Box>
              <Typography sx={{ ...microLabelSx, color: "text.secondary", mb: 0.4 }}>
                Direction right (mils)
              </Typography>
              <TextField
                type="number" size="small" fullWidth
                value={form.direction_right} onChange={setField("direction_right")}
                placeholder="—" sx={numField(theme)}
              />
            </Box>
            {isTime && (
              <Box sx={{ gridColumn: "1 / span 2" }}>
                <Typography sx={{ ...microLabelSx, color: "text.secondary", mb: 0.4 }}>
                  Fuze setting min (Time fuze only)
                </Typography>
                <TextField
                  type="number" size="small" fullWidth
                  value={form.fuze_setting_min} onChange={setField("fuze_setting_min")}
                  placeholder="—" sx={numField(theme)}
                />
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 0.5 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddRoundedIcon />}
              disabled={loading || !missionId}
              onClick={submit}
              sx={(t) => ({
                minHeight: 36,
                fontWeight: 700,
                fontSize: 13,
                textTransform: "none",
                color: t.palette.mode === "dark" ? "#11150f" : "#f7f7f2",
                bgcolor: t.palette.warning.main,
                "&:hover": { bgcolor: t.palette.warning.dark },
              })}
            >
              {loading ? "Saving…" : "Save clearance"}
            </Button>
          </Box>
        </Stack>
      </Paper>

      {/* Existing clearances */}
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <Typography sx={{ ...microLabelSx, color: "text.secondary" }}>
            Defined clearances
          </Typography>
          <Chip
            size="small"
            label={`${clearances.length} entr${clearances.length === 1 ? "y" : "ies"}`}
            sx={(t) => ({
              height: 20,
              fontSize: 10.5,
              fontWeight: 700,
              border: "1px solid",
              borderColor: alpha(t.palette.divider, 0.95),
            })}
          />
        </Stack>
        {clearances.length === 0 ? (
          <Typography variant="body2" sx={{ color: "text.secondary", py: 1 }}>
            No clearances defined for this mission yet.
          </Typography>
        ) : (
          <Box sx={{ overflowX: "auto", border: `1px solid ${alpha(theme.palette.divider, 0.9)}`, borderRadius: 1 }}>
            <Table size="small" sx={{ "& td, & th": { borderBottom: 0 }, minWidth: 600 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={tableHeadCellSx}>Proj</TableCell>
                  <TableCell sx={tableHeadCellSx}>Fuze</TableCell>
                  <TableCell sx={tableHeadCellSx} align="right">QE min</TableCell>
                  <TableCell sx={tableHeadCellSx} align="right">QE max</TableCell>
                  <TableCell sx={tableHeadCellSx} align="right">Dir L</TableCell>
                  <TableCell sx={tableHeadCellSx} align="right">Dir R</TableCell>
                  <TableCell sx={tableHeadCellSx} align="right">Fuze min</TableCell>
                  <TableCell sx={(t) => ({ ...tableHeadCellSx(t), width: 80 })}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clearances.map((c) => (
                  <TableRow
                    key={`${c.projectile}-${c.fuze_type}`}
                    hover
                    onClick={() => editFromExisting(c)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell sx={(t) => ({ ...tableBodyCellSx(t), fontWeight: 700 })}>{c.projectile}</TableCell>
                    <TableCell sx={(t) => ({ ...tableBodyCellSx(t), fontWeight: 700 })}>{c.fuze_type}</TableCell>
                    <TableCell sx={tableBodyCellSx} align="right">{c.qe_min ?? "—"}</TableCell>
                    <TableCell sx={tableBodyCellSx} align="right">{c.qe_max ?? "—"}</TableCell>
                    <TableCell sx={tableBodyCellSx} align="right">{c.direction_left ?? "—"}</TableCell>
                    <TableCell sx={tableBodyCellSx} align="right">{c.direction_right ?? "—"}</TableCell>
                    <TableCell sx={tableBodyCellSx} align="right">{c.fuze_setting_min ?? "—"}</TableCell>
                    <TableCell
                      sx={(t) => ({ ...tableBodyCellSx(t), p: 0.5 })}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Tooltip arrow title="Delete this clearance">
                        <IconButton
                          size="small"
                          onClick={() => removeClearance(c)}
                          sx={{ color: "text.secondary", "&:hover": { color: "error.main" } }}
                        >
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    </Stack>
  );
}
